import { animate, stagger, inView } from 'motion'
import ease from '@/utils/ease'
import 'keen-slider/keen-slider.min.css'
import KeenSlider from 'keen-slider'

export default () => ({
	currentOpenAccordion: null,
	sliderInstances: [],

	init() {
		this.animateLines()
		this.animateVariants()
		this.animateImageShuffle()
		this.initCarousels()
		this.initGalleries()
	},

	openAccordion(index) {
		this.currentOpenAccordion = this.currentOpenAccordion === index ? null : index
	},

	animateLines() {
		const horizontalLines = this.$el.querySelectorAll('[data-horizontal-line]')
		const verticalLines = this.$el.querySelectorAll('[data-vertical-line]')

		if (horizontalLines?.length) {
			inView(
				this.$el,
				() =>
					animate(
						horizontalLines,
						{ scaleX: 1 },
						{ duration: 0.4, delay: 0.2, easing: ease.inOutQuint },
					),
				{ amount: 'some' },
			)
		}
		if (verticalLines?.length) {
			inView(
				this.$el,
				() =>
					animate(
						verticalLines,
						{ scaleY: 1 },
						{ duration: 0.4, delay: 0.2, easing: ease.inOutQuint },
					),
				{ amount: 'some' },
			)
		}
	},

	animateVariants() {
		const opacityVariants = this.$el.querySelectorAll('[data-animate-variant]')
		const clipPathVariants = this.$el.querySelectorAll('[data-animate-variant--clip]')

		if (!opacityVariants?.length && !clipPathVariants?.length) return

		if (opacityVariants?.length) {
			setTimeout(() => {
				inView(
					this.$el,
					() =>
						animate(
							opacityVariants,
							{ opacity: 1 },
							{ duration: 0.4, delay: stagger(0.075), easing: ease.inOutQuint },
						),
					{ amount: 'some' },
				)
			}, 200)
		}

		if (clipPathVariants?.length) {
			setTimeout(() => {
				inView(
					this.$el,
					() =>
						animate(
							clipPathVariants,
							{ opacity: 1, clipPath: 'inset(0px)' },
							{ duration: 0.6, delay: stagger(0.075), easing: ease.inOutQuint },
						),
					{ amount: 'some' },
				)
			}, 200)
		}
	},

	animateImageShuffle() {
		const shuffleImages = this.$el.querySelectorAll('[data-image-shuffle]')
		const fadeImages = this.$el.querySelectorAll('[data-image-fade]')
		const isFadeEffect = fadeImages?.length
		if (!shuffleImages?.length && !isFadeEffect) return

		let currentIndex = 0
		const images = isFadeEffect ? fadeImages : shuffleImages
		const transitionDuration = isFadeEffect ? 1 : 0
		const intervalTime = isFadeEffect ? 5000 : 1000
		const transitionEasing = isFadeEffect ? { easing: ease.inOutQuint } : {}

		// Set initial state
		if (fadeImages?.length) {
			animate(fadeImages, { opacity: 0 }, { duration: 0, ...transitionEasing })
			animate(fadeImages[0], { opacity: 1 }, { duration: 0, ...transitionEasing })
		}

		setInterval(() => {
			currentIndex = (currentIndex + 1) % images.length

			// Single animation block for both cases
			animate(images, { opacity: 0 }, { duration: transitionDuration, ...transitionEasing })
			animate(
				images[currentIndex],
				{ opacity: 1 },
				{ duration: transitionDuration, ...transitionEasing },
			)
		}, intervalTime)
	},

	initCarousels() {
		const carousels = this.$el.querySelectorAll('[data-carousel]')
		if (!carousels?.length) return

		// Wait for next frame to ensure layout is complete
		requestAnimationFrame(() => {
			carousels.forEach((carousel) => {
				const slider = carousel.querySelector('.keen-slider')
				if (!slider) return

				const isCollectionList = carousel.hasAttribute('data-collection-list')

				let sliderConfig: any = {
					slides: { perView: 1.5, spacing: 10 },
				}

				if (isCollectionList) {
					// Collection list specific settings
					sliderConfig = {
						breakpoints: {
							'(min-width: 1000px)': {
								slides: { perView: 3, spacing: 10 },
							},
						},
						slides: { perView: 2.35, spacing: 10 },
					}
				} else {
					// Regular featured collection settings
					sliderConfig = {
						breakpoints: {
							'(min-width: 800px)': {
								slides: { perView: 2, spacing: 24 },
							},
							'(min-width: 1000px)': {
								slides: { perView: 4, spacing: 24 },
							},
						},
						slides: { perView: 1.45, spacing: 12 },
					}
				}

				const instance = new KeenSlider(slider, sliderConfig)
				this.sliderInstances.push(instance)
			})
		})
	},

	initGalleries() {
		const leftGallery = this.$el.querySelector('[data-left-gallery]')
		const rightGallery = this.$el.querySelector('[data-right-gallery]')

		if (leftGallery) {
			this.animateGallery(leftGallery, 5000, 0)
		}

		if (rightGallery) {
			this.animateGallery(rightGallery, 5000, 2500)
		}
	},
	animateGallery(gallery, duration, delay) {
		const items = gallery.querySelectorAll('[data-gallery-item]')

		if (!items || items.length === 0) return

		let currentIndex = 0

		items.forEach((item, index) => {
			item.style.opacity = index === 0 ? '1' : '0'
			item.style.transition = 'opacity 0.35s ease-in-out'
		})

		const showNext = () => {
			const previousIndex = currentIndex
			currentIndex = (currentIndex + 1) % items.length

			items[previousIndex].style.opacity = '0'
			items[currentIndex].style.opacity = '1'
		}

		setTimeout(() => {
			setInterval(showNext, duration)
		}, delay)
	},
})
